import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../services/apiClient";
import {
  RiseOutlined,
  FallOutlined,
  DollarOutlined,
  BarChartOutlined,
  PieChartOutlined,
  ArrowRightOutlined,
  StarOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { message, Spin } from "antd";
import TwoFactorAuthPopup from "../TwoFactorAuthPopup";
import { ThemeContext } from "../../context/ThemeContext";
import { useUser } from "../../context/UserContext";
import "./Dashboard.css";

const ISIN_FETCH_LIMIT = 4;
const ISIN_FETCH_POOL_SIZE = 100;
const HIGH_QUALITY_LIQUID_BONDS_CATEGORY = "highqualityliquidbonds";
const baseUrl =
  process.env.REACT_APP_BASE_URL || "http://localhost:8083/api/v1/sb";

const pickUniqueIssuerBonds = (bonds, limit) => {
  const seenIssuers = new Set();
  const unique = [];

  for (const bond of bonds) {
    const issuer = bond.companyIssuerName?.trim();
    if (!issuer) continue;

    const issuerKey = issuer.toLowerCase();
    if (seenIssuers.has(issuerKey)) continue;

    seenIssuers.add(issuerKey);
    unique.push(bond);
    if (unique.length >= limit) break;
  }

  return unique;
};

  const ExternalUserDashboard = () => {
  const navigate = useNavigate();
    const { isDark } = useContext(ThemeContext);
  const { userData, loading } = useUser();
    const [show2FAPopup, setShow2FAPopup] = useState(false);
    const [userEmail, setUserEmail] = useState("");
  const [topBonds, setTopBonds] = useState([]);
  const [bondsLoading, setBondsLoading] = useState(true);

    useEffect(() => {
    document.documentElement.setAttribute("data-theme", isDark ? "dark" : "light");
    }, [isDark]);

  useEffect(() => {
    check2FAStatus();
    fetchTopIsins();
  }, []);

  const fetchTopIsins = async () => {
    setBondsLoading(true);
    try {
      const response = await apiClient.get(
        `${baseUrl}/isin-category/category/${HIGH_QUALITY_LIQUID_BONDS_CATEGORY}?page=0&size=${ISIN_FETCH_POOL_SIZE}`
      );
      const result = response.data;
      let content = [];

      if (
        result.statusCode === 200 &&
        result.data?.content &&
        Array.isArray(result.data.content)
      ) {
        content = result.data.content;
      } else if (Array.isArray(result)) {
        content = result;
      }

      setTopBonds(pickUniqueIssuerBonds(content, ISIN_FETCH_LIMIT));
    } catch (error) {
      console.error("Error fetching ISINs for dashboard:", error);
      setTopBonds([]);
    } finally {
      setBondsLoading(false);
    }
  };

  const check2FAStatus = async () => {
    try {
      const authBaseUrl =
        process.env.REACT_APP_AUTH_URL || "http://localhost:8080/api";
      const response = await apiClient.get(`${authBaseUrl}/2fa/status`);
      const statusData =
        response.data.success && response.data.data
          ? response.data.data
          : response.data;

      if (statusData.methods && statusData.methods.length === 0) {
        const popupShown = localStorage.getItem("2faPopupShown");
        if (!popupShown) {
          setShow2FAPopup(true);
          setUserEmail(localStorage.getItem("userEmail") || "");
          await fetchTwoFAMethods();
          localStorage.setItem("2faPopupShown", "true");
        }
      } else {
        setShow2FAPopup(false);
      }
    } catch (error) {
      console.error("Error checking 2FA status:", error);
    }
  };

  const fetchTwoFAMethods = async () => {
    try {
      const authBaseUrl =
        process.env.REACT_APP_AUTH_URL || "http://localhost:8080/api";
      await apiClient.get(`${authBaseUrl}/2fa/methods`);
    } catch (error) {
      console.error("Error fetching 2FA methods:", error);
    }
  };

  const handleSkip2FA = () => {
    message.success("2FA setup skipped");
    setShow2FAPopup(false);
    localStorage.setItem("twoFAHandled", "true");
    localStorage.setItem("2faPopupShown", "true");
  };

  const stats = {
    totalBonds: 1247,
    activeInvestments: 89,
    totalValue: 2450000,
    monthlyReturn: 4.2,
    portfolioGrowth: 12.5,
  };

  const getIsinRating = (isin) => {
    if (Array.isArray(isin.creditRatings) && isin.creditRatings.length > 0) {
      return isin.creditRatings[0].currentRating || "N/A";
    }
    return "N/A";
  };

  const formatDateIndian = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatFaceValue = (value) => {
    if (value == null || value === "") return "—";
    const num = parseFloat(value);
    if (!Number.isFinite(num)) return "—";
    return `₹${num.toLocaleString("en-IN")}`;
  };

  const StatCard = ({ title, value, change, icon, color = "primary" }) => (
    <div className="modern-card stat-card">
      <div className="stat-header">
        <div
          className="stat-icon"
          style={{
            backgroundColor: `var(--${color}-100)`,
            color: `var(--${color}-600)`,
          }}
        >
          {icon}
        </div>
        <div className="stat-change">
          {change > 0 ? (
            <span className="change-positive">
              <RiseOutlined /> +{change}%
            </span>
          ) : (
            <span className="change-negative">
              <FallOutlined /> {change}%
            </span>
          )}
        </div>
      </div>
      <div className="stat-content">
        <h3 className="stat-value">{value}</h3>
        <p className="stat-title">{title}</p>
      </div>
    </div>
  );

  const BondCard = ({ bond }) => (
    <div
      className="bond-item bond-item--clickable"
      onClick={() => navigate(`/isin-details/${bond.isin}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          navigate(`/isin-details/${bond.isin}`);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="bond-header">
        <h4 className="bond-name">{bond.isin}</h4>
        <div className="bond-rating">
          <StarOutlined />
          <span>{getIsinRating(bond)}</span>
        </div>
      </div>
      <div className="bond-details">
        <div className="bond-issuer">
          {bond.companyIssuerName ||"—"}
        </div>
        <div className="bond-metrics">
          <div className="metric">
            <span className="metric-label">Coupon</span>
            <span className="metric-value">
              {bond.couponRate != null ? `${bond.couponRate}%` : "—"}
            </span>
          </div>
          <div className="metric">
            <span className="metric-label">Face Value</span>
            <span className="metric-value">{formatFaceValue(bond.faceValue)}</span>
          </div>
          <div className="metric">
            <span className="metric-label">Maturity</span>
            <span className="metric-value">{formatDateIndian(bond.redemption)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const displayName = loading
    ? "..."
    : userData.firstName || userData.username || "User";

  return (
    <div className="modern-dashboard">
      <section className={`dashboard-hero${isDark ? " dashboard-hero--dark" : ""}`}>
        <div className="dashboard-hero__blobs" aria-hidden="true">
          <div className="dashboard-hero__blob dashboard-hero__blob--1" />
          <div className="dashboard-hero__blob dashboard-hero__blob--2" />
        </div>
        <div className="dashboard-hero__inner">
          <span className="dashboard-hero__badge">Your fixed income workspace</span>
          <h1 className="dashboard-hero__title">
            Welcome back, <span>{displayName}</span>
          </h1>
          <p className="dashboard-hero__subtitle">
            Track your portfolio, explore bonds, and stay ahead with market intelligence — all in one place.
          </p>
          <div className="dashboard-hero__actions">
            <button
              type="button"
              className="dashboard-hero__btn dashboard-hero__btn--primary"
              onClick={() => navigate("/construct-portfolio")}
            >
              <PieChartOutlined />
              <span>Portfolio</span>
              <ArrowRightOutlined className="dashboard-hero__btn-arrow" />
            </button>
            <button
              type="button"
              className="dashboard-hero__btn dashboard-hero__btn--secondary"
              onClick={() => navigate("/market-insights")}
            >
              <BarChartOutlined />
              <span>Market Analysis</span>
            </button>
          </div>
        </div>
      </section>

      <div className="stats-grid">
        <StatCard
          title="Total Bonds"
          value={stats.totalBonds.toLocaleString()}
          change={2.1}
          icon={<SafetyOutlined />}
          color="primary"
        />
        <StatCard
          title="Active Investments"
          value={stats.activeInvestments}
          change={-1.2}
          icon={<BarChartOutlined />}
          color="success"
        />
        <StatCard
          title="Portfolio Value"
          value={`₹${(stats.totalValue / 100000).toFixed(1)}L`}
          change={stats.portfolioGrowth}
          icon={<DollarOutlined />}
          color="warning"
        />
        <StatCard
          title="Monthly Return"
          value={`${stats.monthlyReturn}%`}
          change={0.8}
          icon={<RiseOutlined />}
          color="success"
        />
      </div>

      <div className="dashboard-bonds-section">
        <div className="modern-card dashboard-bonds-card">
          <div className="card-header">
            <h3 className="card-title">Top Performing Bonds</h3>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => navigate("/isins")}
            >
              View All
            </button>
          </div>
          <div className="bonds-list dashboard-bonds-grid">
            {bondsLoading ? (
              <div className="dashboard-bonds-loading">
                <Spin size="large" />
          </div>
            ) : topBonds.length > 0 ? (
              topBonds.map((bond) => <BondCard key={bond.isin} bond={bond} />)
            ) : (
              <p className="dashboard-bonds-empty">No instruments available right now.</p>
            )}
           </div>
        </div>
      </div>

      <TwoFactorAuthPopup
        visible={show2FAPopup}
        onClose={() => {
          setShow2FAPopup(false);
          localStorage.setItem("twoFAHandled", "true");
        }}
        email={userEmail}
        onSkip={handleSkip2FA}
      />
    </div>
  );
};

export default ExternalUserDashboard;
